import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { archiveApi } from '@/services/api';
import type { ArchiveEntry } from '@/types/archive';

/**
 * The account's Archive (task 865) — every deleted resource, newest first, and
 * the restore that brings one back with everything that went with it.
 *
 * `staleTime: 0` on purpose: the list is the other half of every delete in the
 * app, so a User who deletes something and follows the toast here must see it,
 * not a cached view from before.
 */
export const useArchive = () =>
  useQuery<ArchiveEntry[]>({
    queryKey: ['archive'],
    queryFn: () => archiveApi.list().then((res) => res.data.data),
    staleTime: 0,
  });

/**
 * Restore one ledger entry. Deliberately has NO `onError` — the rejection
 * travels to whoever called `mutateAsync`, because the Archive page renders the
 * three refusals (`NAME_IN_USE`, `DEPENDENCY_ARCHIVED`, and the non-restorable
 * `422`) as different things a User can act on. Swallowing it here would leave
 * every one of them as "something went wrong".
 *
 * Success invalidates every list in the app rather than the restored entity's
 * one: a ledger entry names its type at runtime, and a restore that brought an
 * AI Service back while the services list still showed it gone would be a
 * worse bug than a few extra refetches.
 */
export const useRestoreDeletion = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (deletionId: string) => archiveApi.restore(deletionId).then((res) => res.data.data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['archive'] });
      void queryClient.invalidateQueries();
    },
  });
};
